import {
  ENVIRONMENT_INTEGRATIONS,
  ENVIRONMENTS_INTEGRATIONS,
  FIVETRAN_CONFIG,
} from '@elementary/common-ui/constants/queryKeys';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from 'shared/services/settingsApi';
import useSnackbar from 'shared/hooks/useSnackbar';
import {
  CreateFivetranIntegrationResponse,
  FivetranDestinationGroups,
  PatchFivetranIntegrationResponse,
} from 'shared/types';

type Props = {
  envId: string;
  integrationId?: string;
  isOpen: boolean;
};

type ConnectParams = {
  apiKey: string;
  apiSecret: string;
} | null;

const useFivetranDialog = ({ envId, integrationId, isOpen }: Props) => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const configQuery = useQuery(
    [FIVETRAN_CONFIG, envId, integrationId],
    () => api.fetchFivetranIntegration(envId, integrationId!),
    { enabled: isOpen && !!integrationId },
  );

  // Retrieves destination groups.
  // Pass null to use stored creds (M2 edit auto-fetch via GET).
  // Pass credentials to use explicit creds (manual Connect via POST).
  const connectMutation = useMutation<
    FivetranDestinationGroups,
    unknown,
    ConnectParams
  >((creds) => {
    if (creds === null) {
      return api.listFivetranDestinationGroups(envId, integrationId!);
    }
    return api.listFivetranDestinationGroupsByKeySecret(
      envId,
      creds.apiKey,
      creds.apiSecret,
    );
  });

  const saveMutation = useMutation<
    CreateFivetranIntegrationResponse | PatchFivetranIntegrationResponse,
    unknown,
    {
      destinationGroupIds: string[];
      apiKey?: string;
      apiSecret?: string;
    }
  >(
    async ({ destinationGroupIds, apiKey, apiSecret }) => {
      if (integrationId) {
        return api.patchFivetranIntegration(
          envId,
          integrationId,
          destinationGroupIds,
          apiKey,
          apiSecret,
        );
      }
      return api.createFivetranIntegration(
        envId,
        apiKey!,
        apiSecret!,
        destinationGroupIds,
      );
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries([ENVIRONMENT_INTEGRATIONS, envId]);
        queryClient.invalidateQueries([ENVIRONMENTS_INTEGRATIONS]);
        queryClient.invalidateQueries([
          FIVETRAN_CONFIG,
          envId,
          data.integration_id,
        ]);

        if (data.failed_webhook_group_ids.length > 0) {
          const count = data.failed_webhook_group_ids.length;
          openSnackbar({
            message: `Connected, but couldn't register webhooks for ${pluralizeString(
              count,
              'destination group',
              'destination groups',
            )}`,
            variant: 'warning',
          });
        } else {
          openSnackbar({
            message: integrationId
              ? 'Fivetran connection updated'
              : 'Fivetran connected successfully',
            variant: 'success',
          });
        }
      },
    },
  );

  return {
    configQuery,
    connectMutation,
    saveMutation,
  };
};

export default useFivetranDialog;
